import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  alpha,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import { THEME_COLORS } from '../../shared/constants/theme';
import { getDashboardDarkSurfaceTokens } from '../../utils/dashboardSurfaceSx';
import { getDistributionSectionIconBoxSx } from '../../utils/distributionMobileLayout';
import { getBookingDrawerMetaChipSx, BOOKING_DRAWER_ACCENT } from '../../utils/bookingDrawerLayout';

const ACCENT = THEME_COLORS.PRIMARY;

function formatLastSync(value) {
  if (!value) return 'Never';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return 'Never';
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return 'Never';
  }
}

function MetricTile({ icon: Icon, label, value, isDark, tokens }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1.25,
        borderRadius: 1.5,
        backgroundColor: isDark ? tokens.chipBg : alpha(ACCENT, 0.05),
        border: `1px solid ${tokens.surfaceBorder}`,
        boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.03)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.5,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(ACCENT, isDark ? 0.14 : 0.12),
          color: ACCENT,
          mb: 0.25,
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: tokens.subtleText,
          fontSize: '0.68rem',
          fontWeight: 600,
          letterSpacing: 0.2,
          textTransform: 'uppercase',
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          fontSize: '0.8125rem',
          color: tokens.text,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
        }}
        title={value}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function ChannelSetupCard({
  channel,
  isDark,
  onEdit,
  onDelete,
  deleteDisabled = false,
  deleteTooltip = 'Delete',
}) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  const enabled = channel.enabled !== false;
  const updateMode = channel.update_mode_display || channel.update_mode || '—';
  const rateLimit = `${channel.rate_limit_per_minute || 60}/min`;
  const lastSync = formatLastSync(channel.last_sync_at);

  const cardBorder = enabled
    ? alpha(ACCENT, isDark ? 0.28 : 0.22)
    : isDark
      ? tokens.surfaceBorder
      : THEME_COLORS.DIVIDER_LIGHT;

  return (
    <Card
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        background: isDark ? tokens.surfaceGradient : THEME_COLORS.CARD_BACKGROUND_LIGHT,
        backgroundColor: isDark ? THEME_COLORS.CARD_BACKGROUND_DARK : THEME_COLORS.CARD_BACKGROUND_LIGHT,
        border: `1px solid ${cardBorder}`,
        boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.28s ease, border-color 0.28s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: alpha(ACCENT, isDark ? 0.45 : 0.35),
          boxShadow: isDark
            ? `0 16px 32px rgba(0,0,0,0.35), 0 0 0 1px ${alpha(ACCENT, 0.14)}`
            : `0 12px 24px ${alpha(ACCENT, 0.18)}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: enabled
            ? `linear-gradient(90deg, ${ACCENT} 0%, ${THEME_COLORS.PRIMARY_HOVER} 100%)`
            : isDark
              ? tokens.surfaceBorderStrong
              : THEME_COLORS.DIVIDER_LIGHT,
        },
      }}
    >
      <CardContent sx={{ flex: 1, p: 2.25, pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={getDistributionSectionIconBoxSx(ACCENT, false)}>
            <ShareIcon sx={{ fontSize: 22, color: ACCENT }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, pt: 0.15 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                fontSize: '1rem',
                lineHeight: 1.25,
                color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: 0.75,
              }}
              title={channel.name}
            >
              {channel.name}
            </Typography>
            <Chip
              label={channel.channel_type_display || channel.channel_type}
              size="small"
              sx={{
                ...getBookingDrawerMetaChipSx(isDark),
                height: 22,
                fontSize: '0.68rem',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          </Box>

          {channel.image_path ? (
            <Box
              sx={{
                flexShrink: 0,
                width: 56,
                height: 56,
                p: 0.75,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isDark ? tokens.chipBg : THEME_COLORS.CARD_BACKGROUND_LIGHT,
                border: `1px solid ${tokens.surfaceBorder}`,
                boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)',
              }}
            >
              <img
                src={channel.image_path}
                alt={channel.name}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Box>
          ) : null}
        </Box>

        <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
          <MetricTile icon={BoltOutlinedIcon} label="Update" value={updateMode} isDark={isDark} tokens={tokens} />
          <MetricTile icon={SpeedOutlinedIcon} label="Rate" value={rateLimit} isDark={isDark} tokens={tokens} />
          <MetricTile icon={SyncOutlinedIcon} label="Sync" value={lastSync} isDark={isDark} tokens={tokens} />
        </Stack>

        <Box
          sx={{
            mt: 'auto',
            pt: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: `1px solid ${isDark ? THEME_COLORS.DIVIDER_DARK : THEME_COLORS.DIVIDER_LIGHT}`,
          }}
        >
          <Chip
            size="small"
            label={enabled ? 'Active' : 'Inactive'}
            icon={
              <Box
                component="span"
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: enabled ? ACCENT : THEME_COLORS.TEXT_SECONDARY_DARK,
                  boxShadow: enabled ? `0 0 8px ${alpha(ACCENT, 0.55)}` : 'none',
                  ml: 0.5,
                }}
              />
            }
            sx={{
              height: 26,
              fontWeight: 700,
              fontSize: '0.72rem',
              bgcolor: enabled
                ? alpha(ACCENT, isDark ? 0.14 : 0.1)
                : isDark
                  ? tokens.chipBg
                  : alpha(THEME_COLORS.TEXT_SECONDARY_LIGHT, 0.08),
              color: enabled ? ACCENT : tokens.subtleText,
              border: `1px solid ${enabled ? alpha(ACCENT, 0.32) : tokens.surfaceBorder}`,
              '& .MuiChip-icon': { ml: 0.75 },
            }}
          />

          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              p: 0.35,
              borderRadius: 999,
              bgcolor: isDark ? tokens.chipBg : alpha(ACCENT, 0.06),
              border: `1px solid ${tokens.surfaceBorder}`,
            }}
          >
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={onEdit}
                sx={{
                  color: BOOKING_DRAWER_ACCENT,
                  width: 32,
                  height: 32,
                  '&:hover': { bgcolor: alpha(ACCENT, 0.16) },
                }}
              >
                <EditIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={deleteTooltip}>
              <span>
                <IconButton
                  size="small"
                  onClick={onDelete}
                  disabled={deleteDisabled}
                  sx={{
                    color: 'error.main',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: alpha('#ef4444', 0.14) },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
